import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, KeyRound, UserPlus, Trash2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { PageTitle } from "@/admin/AdminLayout";

export default function AdminsAdmin() {
  const [list, setList] = useState([]);
  const [links, setLinks] = useState({});
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = () => api.get("/admin/admins").then((r) => setList(r.data));
  useEffect(() => { refresh(); }, []);

  const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success("Copied"));
  const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");
  const invite = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/admin/admins/invite", { email });
      setLinks((l) => ({ ...l, [data.id]: data.setup_link }));
      toast[data.emailed ? "success" : "warning"](data.emailed ? `Invitation e-mailed to ${data.email}` : "Admin created, but the e-mail could not be sent — share the link below");
      setEmail(""); refresh();
    } catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };
  const makeLink = async (a) => {
    try { const { data } = await api.post(`/admin/admins/${a.id}/reset-link`); setLinks({ ...links, [a.id]: data.link }); toast[data.emailed ? "success" : "warning"](`${data.type === "setup" ? "Invitation" : "Reset"} link ${data.emailed ? "e-mailed" : "generated (e-mail failed)"}`); refresh(); }
    catch (e) { toast.error(errorText(e)); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Remove admin ${a.email}? They will lose access immediately.`)) return;
    try { await api.delete(`/admin/admins/${a.id}`); toast.success("Admin removed"); refresh(); } catch (e) { toast.error(errorText(e)); }
  };

  return (
    <div data-testid="admin-admins-page">
      <PageTitle>Admins</PageTitle>
      <p className="text-sm text-[var(--muted)] mb-6">Passwords are never stored — only bcrypt hashes. Invitation and reset links are one-time and are sent by e-mail (Banksy Guide sender).</p>
      <form onSubmit={invite} className="flex gap-3 border border-[var(--line)] p-4 mb-8" data-testid="admin-invite-form">
        <input className="field" type="email" required placeholder="new-admin@example.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-invite-email" />
        <button type="submit" disabled={busy} className="btn-accent px-5 py-3 text-xs flex items-center gap-2 whitespace-nowrap" data-testid="admin-invite-submit"><UserPlus size={14} /> Invite admin</button>
      </form>
      <div className="space-y-4">
        {list.map((a) => {
          const link = links[a.id] || a.setup_link;
          return (
            <div key={a.id} className="border border-[var(--line)] p-4" data-testid={`admin-row-${a.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[200px]">
                  <span className="font-semibold" data-testid={`admin-email-${a.id}`}>{a.email}{a.is_me && <span className="text-xs text-[var(--muted)]"> (you)</span>}</span>
                  <p className="text-xs text-[var(--muted)]" data-testid={`admin-last-login-${a.id}`}>Last login: {fmt(a.last_login_at)}{a.invited_by ? ` · invited by ${a.invited_by}` : ""}</p>
                </div>
                <span className={`text-[0.65rem] tracking-widest uppercase px-2 py-0.5 border ${a.status === "active" ? "border-green-600 text-green-500" : "border-[var(--accent)] text-accent"}`} data-testid={`admin-status-${a.id}`}>{a.status}</span>
                <button type="button" onClick={() => makeLink(a)} className="border border-[var(--line)] px-3 py-1.5 text-xs flex items-center gap-2 hover:border-white transition-colors" data-testid={`admin-make-link-${a.id}`}><KeyRound size={14} /> {a.status === "active" ? "Send reset link" : "Resend invitation"}</button>
                <button type="button" onClick={() => remove(a)} disabled={a.is_me || list.length <= 1} title={a.is_me ? "You cannot remove yourself" : list.length <= 1 ? "The last admin cannot be removed" : "Remove admin"} className="ctrl !w-9 !h-9 text-[var(--muted)] hover:text-accent disabled:opacity-30" aria-label="Remove admin" data-testid={`admin-remove-${a.id}`}><Trash2 size={16} /></button>
              </div>
              {link && (
                <div className="mt-3 flex items-center gap-2 text-xs break-all bg-[var(--surface)] p-3" data-testid={`admin-link-${a.id}`}>
                  <a href={link} className="text-accent underline flex-1">{link}</a>
                  <button type="button" onClick={() => copy(link)} className="ctrl !w-8 !h-8" aria-label="Copy link"><Copy size={14} /></button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
